import { Controller } from "@hotwired/stimulus";

// Handles geolocation and geocoding via Apple MapKit.
// Provides "use my location" functionality and address-to-coordinates conversion.
export default class extends Controller {
  static targets = ["input", "latField", "lngField"];

  connect() {
    this.geocoder = null;
  }

  async geocode() {
    const address = this.inputTarget.value?.trim();
    if (!address || address.length < 3) return;

    try {
      await this.ensureGeocoder();

      // US-only directory; without this, zip "31407" (Savannah, GA) geocodes
      // to a Singapore postal district and the user gets zero results.
      const options = { limitToCountries: "US" };

      const result = await new Promise((resolve, reject) => {
        this.geocoder.lookup(
          address,
          (error, data) => {
            if (error) {
              reject(error);
              return;
            }
            if (!data.results || data.results.length === 0) {
              reject(new Error("No results found"));
              return;
            }
            resolve(data.results[0]);
          },
          options,
        );
      });

      this.updateLocation(
        result.coordinate.latitude,
        result.coordinate.longitude,
      );
    } catch (error) {
      console.warn("Geocoding failed:", error.message);
    }
  }

  async getCurrentPosition() {
    if (!navigator.geolocation) {
      console.warn("Geolocation not supported");
      return;
    }

    // Show loading state
    this.inputTarget.value = "Locating...";
    this.inputTarget.disabled = true;

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000, // Cache for 5 minutes
        });
      });

      this.updateLocation(position.coords.latitude, position.coords.longitude);
      this.inputTarget.value = "Current Location";

      // Auto-submit the form
      const form = this.element.closest("form");
      if (form) {
        form.requestSubmit();
      }
    } catch (error) {
      console.warn("Geolocation failed:", error.message);
      this.inputTarget.value = "";
    } finally {
      this.inputTarget.disabled = false;
    }
  }

  updateLocation(lat, lng) {
    if (this.hasLatFieldTarget) {
      this.latFieldTarget.value = lat;
    }
    if (this.hasLngFieldTarget) {
      this.lngFieldTarget.value = lng;
    }

    // Dispatch event for other controllers
    this.dispatch("changed", { detail: { lat, lng } });
  }

  async ensureGeocoder() {
    if (this.geocoder) return;

    // Wait for MapKit to be available
    await this.waitForMapKit();

    this.geocoder = new mapkit.Geocoder({ language: "en" });
  }

  waitForMapKit() {
    return new Promise((resolve, reject) => {
      // Check if MapKit is already initialized
      if (typeof mapkit !== "undefined" && mapkit.loadedLibraries?.length > 0) {
        resolve();
        return;
      }

      // Set a timeout
      const timeout = setTimeout(() => {
        reject(new Error("MapKit initialization timeout"));
      }, 10000);

      // Listen for mapkit initialization
      const checkMapKit = setInterval(() => {
        if (
          typeof mapkit !== "undefined" &&
          mapkit.loadedLibraries?.length > 0
        ) {
          clearInterval(checkMapKit);
          clearTimeout(timeout);
          resolve();
        }
      }, 100);
    });
  }
}
